import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calendar, MapPin, Car, CreditCard, TrendingUp, Users, CheckCircle, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [updateStatus, setUpdateStatus] = useState("");
  const [updateDepositPaid, setUpdateDepositPaid] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, config),
        axios.get(`${API}/admin/stats`, config)
      ]);
      
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBooking = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/bookings/${selectedBooking.id}`,
        {
          status: updateStatus || undefined,
          deposit_paid: updateDepositPaid
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Booking updated successfully");
      fetchData();
      setSelectedBooking(null);
    } catch (error) {
      console.error("Error updating booking:", error);
      toast.error("Failed to update booking");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed": return "bg-green-500";
      case "pending": return "bg-yellow-500";
      case "completed": return "bg-blue-500";
      case "cancelled": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img 
                src="/elite-cwb-logo.webp" 
                alt="Elite CWB Logo" 
                className="h-24 w-auto" style={{ filter: 'drop-shadow(0 0 10px rgba(212, 175, 55, 0.3))' }}
              />
            </div>
            <div className="flex items-center gap-4">
              <Badge className="bg-amber-600 text-white">ADMIN</Badge>
              <span className="text-gray-700">Welcome, <strong>{user.name}</strong></span>
              <Button 
                data-testid="logout-admin-btn"
                variant="outline" 
                onClick={onLogout}
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Manage all bookings and operations</p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card className="shadow-lg border-2">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Bookings</p>
                      <p className="text-3xl font-bold text-gray-900">{stats.total_bookings}</p>
                    </div>
                    <Car className="w-10 h-10 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="shadow-lg border-2">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Pending</p>
                      <p className="text-3xl font-bold text-yellow-600">{stats.pending_bookings}</p>
                    </div>
                    <Clock className="w-10 h-10 text-yellow-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="shadow-lg border-2">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Confirmed</p>
                      <p className="text-3xl font-bold text-green-600">{stats.confirmed_bookings}</p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-green-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="shadow-lg border-2">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                      <p className="text-3xl font-bold text-amber-600">£{stats.total_revenue}</p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-amber-400" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Bookings List */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading bookings...</p>
            </div>
          ) : bookings.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No bookings yet</h3>
                <p className="text-gray-600">Bookings will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">All Bookings</h2>
              <div className="grid gap-6">
                {bookings.map((booking) => (
                  <Card key={booking.id} className="shadow-lg border-2 hover:shadow-xl transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-2xl">{booking.vehicle_type}</CardTitle>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={getStatusColor(booking.status)}>
                              {booking.status.toUpperCase()}
                            </Badge>
                            {booking.deposit_paid && (
                              <Badge className="bg-green-600">Deposit Paid</Badge>
                            )}
                            {!booking.user_id && (
                              <Badge variant="outline">Guest Booking</Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-gray-900">£{booking.total_price}</div>
                          <div className="text-sm text-gray-600">Total</div>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                data-testid={`manage-booking-btn-${booking.id}`}
                                onClick={() => {
                                  setSelectedBooking(booking);
                                  setUpdateStatus(booking.status);
                                  setUpdateDepositPaid(booking.deposit_paid);
                                }}
                                variant="outline"
                                className="mt-2"
                              >
                                Manage
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Manage Booking</DialogTitle>
                                <DialogDescription>Update booking status and payment</DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div>
                                  <Label>Booking Status</Label>
                                  <Select value={updateStatus} onValueChange={setUpdateStatus}>
                                    <SelectTrigger data-testid="admin-status-select" className="mt-2">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="pending">Pending</SelectItem>
                                      <SelectItem value="confirmed">Confirmed</SelectItem>
                                      <SelectItem value="completed">Completed</SelectItem>
                                      <SelectItem value="cancelled">Cancelled</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Checkbox 
                                    data-testid="admin-deposit-checkbox"
                                    id="depositPaid" 
                                    checked={updateDepositPaid} 
                                    onCheckedChange={setUpdateDepositPaid}
                                  />
                                  <Label htmlFor="depositPaid">Deposit Paid</Label>
                                </div>
                                <Button 
                                  data-testid="save-booking-changes-btn"
                                  onClick={handleUpdateBooking}
                                  className="w-full bg-gradient-to-r from-gray-900 to-gray-700 text-white hover:from-gray-800 hover:to-gray-600"
                                >
                                  Save Changes
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-900">Journey Details</h4>
                          <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-gray-600 mt-0.5" />
                            <div>
                              <div className="text-sm text-gray-600">Pickup</div>
                              <div className="font-medium">{booking.pickup_location}</div>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-gray-600 mt-0.5" />
                            <div>
                              <div className="text-sm text-gray-600">Drop-off</div>
                              <div className="font-medium">{booking.dropoff_location}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-gray-600" />
                            <div>
                              <div className="text-sm text-gray-600">Date & Time</div>
                              <div className="font-medium">{booking.date} at {booking.time}</div>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-900">Contact Information</h4>
                          {booking.guest_name ? (
                            <>
                              <div>
                                <div className="text-sm text-gray-600">Name</div>
                                <div className="font-medium">{booking.guest_name}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-600">Email</div>
                                <div className="font-medium">{booking.guest_email}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-600">Phone</div>
                                <div className="font-medium">{booking.guest_phone}</div>
                              </div>
                            </>
                          ) : (
                            <div className="text-gray-600">Registered user booking</div>
                          )}
                        </div>
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-900">Payment Details</h4>
                          <div>
                            <div className="text-sm text-gray-600">Total Price</div>
                            <div className="font-bold text-lg">£{booking.total_price}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Deposit (20%)</div>
                            <div className="font-bold text-lg text-amber-700">£{booking.deposit_amount}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Remaining</div>
                            <div className="font-medium">£{booking.total_price - booking.deposit_amount}</div>
                          </div>
                        </div>
                      </div>
                      {booking.notes && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm text-gray-600 mb-1">Notes</div>
                          <div className="font-medium">{booking.notes}</div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;